import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SEOToolsHubHeader from './seo-tools-hub/SEOToolsHubHeader';
import SEOAnalyzerTab from './seo-tools-hub/SEOAnalyzerTab';
import WebTrafficAnalyticsTab from './seo-tools-hub/WebTrafficAnalyticsTab';
import KeywordResearchTab from './seo-tools-hub/KeywordResearchTab';
import CompetitorAnalysisTab from './seo-tools-hub/CompetitorAnalysisTab';
import BacklinkMonitorTab from './seo-tools-hub/BacklinkMonitorTab';
import SERPTrackerTab from './seo-tools-hub/SERPTrackerTab';
import IntegrationsTab from './seo-tools-hub/IntegrationsTab';

export type SEOToolsHubTabType = 'seo-analyzer' | 'web-traffic' | 'keyword-research' | 'competitor-analysis' | 'backlink-monitor' | 'serp-tracker' | 'integrations';

const SEOToolsHub: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [activeTab, setActiveTab] = useState<SEOToolsHubTabType>('seo-analyzer');

  // Parse hash from URL to set active tab
  useEffect(() => {
    const hash = location.hash.slice(1) as SEOToolsHubTabType;
    if (['seo-analyzer', 'web-traffic', 'keyword-research', 'competitor-analysis', 'backlink-monitor', 'serp-tracker', 'integrations'].includes(hash)) {
      setActiveTab(hash);
    } else {
      setActiveTab('seo-analyzer');
    }
  }, [location.hash]);

  // Update URL hash when tab changes
  const handleTabChange = (tab: SEOToolsHubTabType) => {
    setActiveTab(tab);
    navigate(`#${tab}`, { replace: true });
  };

  // Redirect if not logged in
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <SEOToolsHubHeader />

        {/* Tab Navigation */}
        <div className="mt-6 rounded-2xl bg-white shadow-sm ring-1 ring-gray-100">
          <div className="border-b border-gray-200 px-6">
            <nav className="flex gap-6 overflow-x-auto" aria-label="Tabs">
              <button
                onClick={() => handleTabChange('seo-analyzer')}
                className={`
                  group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition
                  ${activeTab === 'seo-analyzer'
                    ? 'border-green-600 text-green-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }
                `}
                aria-current={activeTab === 'seo-analyzer' ? 'page' : undefined}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                <span>SEO Analyzer</span>
                <span className="ml-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                  Audit
                </span>
              </button>
              
              <button
                onClick={() => handleTabChange('web-traffic')}
                className={`
                  group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition whitespace-nowrap
                  ${activeTab === 'web-traffic'
                    ? 'border-green-600 text-green-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }
                `}
                aria-current={activeTab === 'web-traffic' ? 'page' : undefined}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span>Web Traffic</span>
              </button>
              
              <button
                onClick={() => handleTabChange('keyword-research')}
                className={`
                  group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition whitespace-nowrap
                  ${activeTab === 'keyword-research'
                    ? 'border-green-600 text-green-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }
                `}
                aria-current={activeTab === 'keyword-research' ? 'page' : undefined}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span>Keyword Research</span>
                <span className="ml-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                  Free
                </span>
              </button>
              
              <button
                disabled
                className="group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition whitespace-nowrap border-transparent text-gray-400 cursor-not-allowed opacity-60"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span>Competitor Analysis</span>
                <span className="ml-1 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
                  Coming Soon
                </span>
              </button>
              
              <button
                disabled
                className="group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition whitespace-nowrap border-transparent text-gray-400 cursor-not-allowed opacity-60"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                <span>Backlink Monitor</span>
                <span className="ml-1 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
                  Coming Soon
                </span>
              </button>
              
              <button
                disabled
                className="group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition whitespace-nowrap border-transparent text-gray-400 cursor-not-allowed opacity-60"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                </svg>
                <span>SERP Tracker</span>
                <span className="ml-1 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
                  Coming Soon
                </span>
              </button>
              
              <button
                onClick={() => handleTabChange('integrations')}
                className={`
                  group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition whitespace-nowrap
                  ${activeTab === 'integrations'
                    ? 'border-green-600 text-green-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }
                `}
                aria-current={activeTab === 'integrations' ? 'page' : undefined}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>Integrations</span>
                <span className="ml-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">
                  Setup
                </span>
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'seo-analyzer' && <SEOAnalyzerTab />}
            {activeTab === 'web-traffic' && <WebTrafficAnalyticsTab />}
            {activeTab === 'keyword-research' && <KeywordResearchTab />}
            {activeTab === 'integrations' && <IntegrationsTab />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SEOToolsHub;
